import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getCustomerDiscountTiers,
  GetCustomerDiscountTiersRequest,
  CustomerDiscountTierData,
  deleteCustomerDiscountTier,
} from '@/application/requests/customerDiscountTier';

export interface CustomerDiscountTierState {
  tiers: CustomerDiscountTierData[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  // Delete state
  deleteLoading: boolean;
  deleteError: string | null;
  // All tiers for dropdown (loaded once with limit 1000)
  allTiers: CustomerDiscountTierData[];
  allTiersLoading: boolean;
  allTiersLoaded: boolean;
  allTiersError: string | null;
}

const initialState: CustomerDiscountTierState = {
  tiers: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  hasNext: false,
  hasPrevious: false,
  deleteLoading: false,
  deleteError: null,
  allTiers: [],
  allTiersLoading: false,
  allTiersLoaded: false,
  allTiersError: null,
};

export const fetchCustomerDiscountTiers = createAsyncThunk(
  'customerDiscountTier/fetchTiers',
  async (params: GetCustomerDiscountTiersRequest, { rejectWithValue }) => {
    try {
      const response = await getCustomerDiscountTiers(params);

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to fetch customer discount tiers'
        );
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch customer discount tiers'
      );
    }
  }
);

export const removeCustomerDiscountTier = createAsyncThunk(
  'customerDiscountTier/removeTier',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await deleteCustomerDiscountTier(id);

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to delete customer discount tier'
        );
      }

      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to delete customer discount tier'
      );
    }
  }
);

export const fetchAllCustomerDiscountTiers = createAsyncThunk(
  'customerDiscountTier/fetchAllTiers',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as {
      customerDiscountTier: CustomerDiscountTierState;
    };

    if (state.customerDiscountTier.allTiersLoaded) {
      return state.customerDiscountTier.allTiers;
    }

    try {
      const response = await getCustomerDiscountTiers({
        page: 1,
        limit: 1000,
      });

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to fetch all customer discount tiers'
        );
      }

      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch all customer discount tiers'
      );
    }
  }
);

const customerDiscountTierSlice = createSlice({
  name: 'customerDiscountTier',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
      state.deleteError = null;
      state.allTiersError = null;
    },
    resetCustomerDiscountTiers: () => initialState,
    addToAllTiers: (
      state,
      action: PayloadAction<CustomerDiscountTierData>
    ) => {
      if (state.allTiersLoaded) {
        state.allTiers = [action.payload, ...state.allTiers];
      }
    },
    updateInAllTiers: (
      state,
      action: PayloadAction<CustomerDiscountTierData>
    ) => {
      if (state.allTiersLoaded) {
        const index = state.allTiers.findIndex(
          (d) => d.id === action.payload.id
        );
        if (index !== -1) {
          state.allTiers[index] = action.payload;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerDiscountTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerDiscountTiers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.tiers = action.payload.data;
          state.totalPages = action.payload.total_pages;
          state.totalItems = action.payload.total;
          state.currentPage = action.payload.page;
          state.pageSize = action.payload.limit;
          state.hasNext = action.payload.has_next;
          state.hasPrevious = action.payload.has_previous;
        }
      })
      .addCase(fetchCustomerDiscountTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(removeCustomerDiscountTier.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(removeCustomerDiscountTier.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.tiers = state.tiers.filter((d) => d.id !== action.payload);
        state.totalItems = Math.max(0, state.totalItems - 1);
        if (state.allTiersLoaded) {
          state.allTiers = state.allTiers.filter(
            (d) => d.id !== action.payload
          );
        }
      })
      .addCase(removeCustomerDiscountTier.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      })
      .addCase(fetchAllCustomerDiscountTiers.pending, (state) => {
        if (!state.allTiersLoaded) {
          state.allTiersLoading = true;
          state.allTiersError = null;
        }
      })
      .addCase(fetchAllCustomerDiscountTiers.fulfilled, (state, action) => {
        state.allTiersLoading = false;
        state.allTiers = action.payload;
        state.allTiersLoaded = true;
      })
      .addCase(fetchAllCustomerDiscountTiers.rejected, (state, action) => {
        state.allTiersLoading = false;
        state.allTiersError = action.payload as string;
      });
  },
});

export const {
  setCurrentPage,
  setPageSize,
  clearError,
  resetCustomerDiscountTiers,
  addToAllTiers,
  updateInAllTiers,
} = customerDiscountTierSlice.actions;

export default customerDiscountTierSlice.reducer;
